import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Scale, CheckCircle2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export const DisclaimerModal = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  const clauses = t("disclaimer.clauses");

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="disclaimer-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] grid place-items-center bg-slate-900/55 backdrop-blur-md px-4 py-8"
        >
          <motion.div
            initial={{ scale: 0.94, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 16, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="w-full max-w-2xl bg-white rounded-3xl border border-slate-200 shadow-2xl overflow-hidden"
          >
            <div className="flex items-center gap-4 px-7 sm:px-9 pt-7 sm:pt-8 pb-5 border-b border-slate-100">
              <img src="/qsafe-logo.png" alt="QSafe India" className="h-10 w-auto rounded-lg" />
              <div>
                <div className="flex items-center gap-2">
                  <Scale className="w-4 h-4 text-blue-700" />
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">QSafe India</p>
                </div>
                <h2 className="mt-1 text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
                  {t("disclaimer.title")}
                </h2>
              </div>
            </div>

            <div data-lenis-prevent className="px-7 sm:px-9 py-6 max-h-[46vh] overflow-y-auto space-y-5">
              {clauses.map((c, i) => (
                <div key={i} className="flex gap-4">
                  <span className="shrink-0 grid place-items-center w-8 h-8 rounded-full bg-blue-50 border border-blue-200 font-mono text-xs font-extrabold text-blue-700">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{c.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{c.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-7 sm:px-9 py-6 border-t border-slate-100 bg-slate-50/60">
              <button
                data-testid="disclaimer-agree-button"
                onClick={() => setOpen(false)}
                className="w-full inline-flex justify-center items-center gap-2 text-sm font-bold text-white bg-blue-700 hover:bg-slate-900 py-4 rounded-full shadow-[0_14px_32px_-12px_rgba(29,78,216,0.55)] transition-all duration-300 hover:-translate-y-0.5"
              >
                <CheckCircle2 className="w-5 h-5" />
                {t("disclaimer.agree")}
              </button>
              <p className="mt-3 text-center text-[11px] text-slate-400">{t("disclaimer.note")}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
